// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Drivers
// Driver-centric view of crew data — manage driver assignments,
// contact info, and associated trucks/zones
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Table, Divider,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconPhone, IconMail, IconUpload, IconDownload, IconFile, IconX,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { fmtDateTime, fmtDate } from '../../lib/date-format';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { DebrisCrew } from '../../types/debris';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'standby', label: 'Standby' },
  { value: 'out_of_service', label: 'Out of Service' },
];

const CREW_TYPES = [
  { value: 'knuckleboom', label: 'Knuckleboom' },
  { value: 'grapple', label: 'Grapple' },
  { value: 'self_loader', label: 'Self-Loader' },
  { value: 'dump_truck', label: 'Dump Truck' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--color-success)', standby: '#C68700', out_of_service: 'var(--color-error)',
};

const EMPTY: Partial<DebrisCrew> = {
  contractorId: '', crewName: '', crewType: '', truckId: '',
  driverName: '', driverPhone: '', driverEmail: '',
  status: 'active', assignedZoneId: '', notes: '',
};

export default function Drivers() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    crews, contractors, trucks, zones, loadingCrews,
    loadCrews, loadContractors, loadTrucks, loadZones,
    createCrew, updateCrew, deleteCrew,
  } = useDebrisStore();

  const { loadTickets, loadLoadTickets } = useDebrisOpsStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [contractorFilter, setContractorFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisCrew | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisCrew | null>(null);
  const [detailTarget, setDetailTarget] = useState<DebrisCrew | null>(null);
  const [form, setForm] = useState<Partial<DebrisCrew>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_FILE_SIZE = 45 * 1024; // 45KB base64 per file

  // Resize an image to max 300px width and return base64 data URL
  function resizeImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const MAX = 300;
        let { width, height } = img;
        if (width > MAX) { height = Math.round(height * MAX / width); width = MAX; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.6));
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  async function handleFileUpload(file: File) {
    const existing: Array<{name: string; type: string; uploadedAt: string; data: string}> =
      JSON.parse(form.resourceFiles || '[]');
    if (existing.length >= 5) {
      notifications.show({ message: 'Maximum 5 files per driver', color: '#C68700' }); return;
    }
    let data = '';
    if (file.type.startsWith('image/')) {
      data = await resizeImage(file);
    } else {
      const ab = await file.arrayBuffer();
      const bytes = new Uint8Array(ab);
      let binary = '';
      bytes.forEach(b => { binary += String.fromCharCode(b); });
      const b64 = btoa(binary);
      if (b64.length > MAX_FILE_SIZE) {
        notifications.show({ message: `File too large (max ~33KB). Storing filename only.`, color: '#C68700' });
        data = ''; // no data, just record the filename
      } else {
        data = `data:${file.type};base64,${b64}`;
      }
    }
    const updated = [...existing, { name: file.name, type: file.type, uploadedAt: new Date().toISOString(), data }];
    setForm(prev => ({ ...prev, resourceFiles: JSON.stringify(updated) }));
    notifications.show({ message: `"${file.name}" added`, color: 'var(--color-success)' });
  }

  function removeFile(idx: number) {
    const existing: Array<object> = JSON.parse(form.resourceFiles || '[]');
    existing.splice(idx, 1);
    setForm(prev => ({ ...prev, resourceFiles: JSON.stringify(existing) }));
  }

  function downloadFile(f: {name: string; type: string; data: string}) {
    if (!f.data) { notifications.show({ message: 'File content not stored (too large)', color: '#C68700' }); return; }
    const a = document.createElement('a');
    a.href = f.data;
    a.download = f.name;
    a.click();
  }

  useEffect(() => {
    loadCrews(); loadContractors(); loadTrucks(); loadZones(); loadLoadTickets();
  }, [loadCrews, loadContractors, loadTrucks, loadZones, loadLoadTickets]);

  const iCrews = incidentId ? crews : [];
  const iContractors = incidentId ? contractors : [];
  const iTrucks = incidentId ? trucks : [];
  const iZones = incidentId ? zones : [];

  // Only show crews that have a driver assigned
  const driversOnly = useMemo(() => iCrews.filter(c => c.driverName), [iCrews]);

  const contractorSelect = useMemo(() =>
    iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
  [iContractors]);
  const truckSelect = useMemo(() =>
    iTrucks.map(t => ({ value: String(t.id), label: `${t.truckNumber} (${t.cubicYardCapacity || '?'} CY)` })),
  [iTrucks]);
  const zoneSelect = useMemo(() =>
    iZones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode || `#${z.id}` })),
  [iZones]);

  const filtered = useMemo(() => {
    let list = driversOnly;
    if (statusFilter && statusFilter !== 'all') list = list.filter(c => c.status === statusFilter);
    if (contractorFilter && contractorFilter !== 'all') list = list.filter(c => c.contractorId === contractorFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.driverName.toLowerCase().includes(q) ||
        c.driverEmail.toLowerCase().includes(q) ||
        c.driverPhone.includes(q) ||
        c.crewName.toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (a.driverName || '').localeCompare(b.driverName || '');
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const numA = Number(va); const numB = Number(vb);
      if (!isNaN(numA) && !isNaN(numB) && va !== '' && vb !== '') return (numA - numB) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [driversOnly, statusFilter, contractorFilter, search, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [statusFilter, contractorFilter, search]);

  const contractorName = (id: string) => contractors.find(c => String(c.id) === id)?.companyName ?? '';
  const truckLabel = (id: string) => { const t = trucks.find(t => String(t.id) === id); return t ? `${t.truckNumber} (${t.truckType || ''})` : ''; };
  const zoneName = (id: string) => zones.find(z => String(z.id) === id)?.zoneName ?? '';
  const setField = (key: keyof DebrisCrew, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  // Count loads for a driver/crew
  const driverLoadCount = useMemo(() => {
    const map: Record<number, { count: number; cy: number }> = {};
    const iTickets = loadTickets;
    iTickets.forEach(t => {
      const crewId = parseInt(t.crewId || '0');
      if (crewId) {
        if (!map[crewId]) map[crewId] = { count: 0, cy: 0 };
        map[crewId].count++;
        map[crewId].cy += parseFloat(t.calculatedCubicYards || '0');
      }
    });
    return map;
  }, [loadTickets, incidentId]);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setModalOpen(true);
  };

  const openEdit = (c: DebrisCrew) => {
    setEditTarget(c);
    setForm({ ...c });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.driverName?.trim()) {
      notifications.show({ message: 'Driver name is required', color: '#C68700' });
      return;
    }
    if (!form.crewName?.trim()) {
      // Auto-generate crew name from driver name
      form.crewName = `${form.driverName}'s Crew`;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId };
      if (editTarget) {
        await updateCrew(editTarget.id, payload);
        notifications.show({ message: 'Driver updated', color: 'var(--color-success)' });
      } else {
        await createCrew(payload);
        notifications.show({ message: 'Driver added', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteCrew(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.driverName} removed`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Drivers</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingCrews} onClick={() => loadCrews()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Driver</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search driver name, email, phone, crew..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
        <Select data={[{ value: 'all', label: 'All Contractors' }, ...contractorSelect]}
          value={contractorFilter ?? 'all'} onChange={v => setContractorFilter(v)} size="xs" w={200} searchable />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} driver{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('driverName')}>Driver{sortIcon('driverName')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Contact</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Contractor</th>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('crewName')}>Crew{sortIcon('crewName')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Truck</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Zone</th>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>Loads</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>CY Hauled</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingCrews ? 'Loading...' : 'No drivers found'}
                </td></tr>
              )}
              {pageRows.map(c => {
                const stats = driverLoadCount[c.id] || { count: 0, cy: 0 };
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                    onClick={() => setDetailTarget(c)}>
                    <td style={{ padding: '8px 16px', fontWeight: 500 }}>{c.driverName}</td>
                    <td style={{ padding: '8px 16px' }}>
                      <Group gap={4}>
                        {c.driverPhone && <Group gap={2}><IconPhone size={12} /><Text size="xs">{c.driverPhone}</Text></Group>}
                        {c.driverEmail && <Group gap={2}><IconMail size={12} /><Text size="xs">{c.driverEmail}</Text></Group>}
                      </Group>
                    </td>
                    <td style={{ padding: '8px 16px' }}>{c.contractorId ? contractorName(c.contractorId) : '-'}</td>
                    <td style={{ padding: '8px 16px' }}>{c.crewName || '-'}</td>
                    <td style={{ padding: '8px 16px' }}>{c.truckId ? truckLabel(c.truckId) : '-'}</td>
                    <td style={{ padding: '8px 16px' }}>{c.assignedZoneId ? zoneName(c.assignedZoneId) : '-'}</td>
                    <td style={{ padding: '8px 16px' }}>
                      <Badge size="sm" color={STATUS_COLORS[c.status] ?? '#939598'}>{c.status?.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>
                      {stats.count}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600 }}>
                      {stats.cy > 0 ? stats.cy.toLocaleString() : '-'}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      {canEdit && (
                      <Group gap={4} justify="flex-end">
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(c)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(c)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="var(--color-primary-700)" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Driver Detail Modal */}
      <Modal opened={!!detailTarget} onClose={() => setDetailTarget(null)}
        title={`Driver: ${detailTarget?.driverName}`} centered size="lg">
        {detailTarget && (
          <Stack>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Phone</Text>
                <Text>{detailTarget.driverPhone || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Email</Text>
                <Text>{detailTarget.driverEmail || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Contractor</Text>
                <Text>{detailTarget.contractorId ? contractorName(detailTarget.contractorId) : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Crew</Text>
                <Text>{detailTarget.crewName || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Truck</Text>
                <Text>{detailTarget.truckId ? truckLabel(detailTarget.truckId) : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Assigned Zone</Text>
                <Text>{detailTarget.assignedZoneId ? zoneName(detailTarget.assignedZoneId) : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Status</Text>
                <Badge color={STATUS_COLORS[detailTarget.status] ?? '#939598'}>{detailTarget.status?.replace(/_/g, ' ')}</Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total Loads / CY</Text>
                <Text fw={600}>
                  {(driverLoadCount[detailTarget.id]?.count ?? 0)} loads &middot; {(driverLoadCount[detailTarget.id]?.cy ?? 0).toLocaleString()} CY
                </Text>
              </div>
            </SimpleGrid>

            {detailTarget.notes && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Notes</Text>
                <Text size="sm">{detailTarget.notes}</Text>
              </div>
            )}

            {/* Resources */}
            {(() => {
              let files: Array<{name: string; type: string; uploadedAt: string; data: string}> = [];
              try { files = JSON.parse(detailTarget.resourceFiles || '[]'); } catch { /* */ }
              if (files.length === 0) return null;
              return (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb="xs">Driver Resources</Text>
                  <Stack gap="xs">
                    {files.map((f, i) => (
                      <Group key={i} gap="sm" p="xs" style={{ border: '1px solid #cbd5e1', borderRadius: 4 }}>
                        {f.type.startsWith('image/') && f.data ? (
                          <img src={f.data} alt={f.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                        ) : (
                          <ActionIcon variant="light" size="xl" color="var(--color-primary-700)" style={{ flexShrink: 0 }}>
                            <IconFile size={20} />
                          </ActionIcon>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={500}>{f.name}</Text>
                          <Text size="xs" c="dimmed">{fmtDate(f.uploadedAt)}</Text>
                        </div>
                        {f.data && (
                          <Button size="compact-xs" variant="light" leftSection={<IconDownload size={12} />}
                            onClick={() => downloadFile(f)}>Download</Button>
                        )}
                      </Group>
                    ))}
                  </Stack>
                </div>
              );
            })()}

            {/* Recent loads */}
            <Text size="sm" fw={600} mt="sm">Recent Load Tickets</Text>
            {(() => {
              const iTickets = loadTickets
                .filter(t => t.crewId === String(detailTarget.id))
                .sort((a, b) => (b.id || 0) - (a.id || 0))
                .slice(0, 10);
              if (iTickets.length === 0) return <Text size="sm" c="dimmed">No load tickets yet</Text>;
              return (
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Ticket</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th ta="right">CY</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Pickup</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {iTickets.map(t => (
                      <Table.Tr key={t.id}>
                        <Table.Td><Text size="sm" fw={500}>{t.ticketNumber}</Text></Table.Td>
                        <Table.Td><Text size="sm">{t.debrisType}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="sm">{parseFloat(t.calculatedCubicYards || '0').toLocaleString()}</Text></Table.Td>
                        <Table.Td><Badge size="xs">{t.status?.replace(/_/g, ' ')}</Badge></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{fmtDateTime(t.pickupTime)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              );
            })()}

            <Group justify="flex-end">
              {canEdit && (
              <Button variant="light" size="xs" onClick={() => { setDetailTarget(null); openEdit(detailTarget); }}>
                Edit Driver
              </Button>
              )}
              <Button variant="default" size="xs" onClick={() => setDetailTarget(null)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Driver' : 'Add Driver'} centered size="lg">
        <Stack>
          <Text size="sm" fw={600} c="dimmed">Driver Info</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Driver Name" required value={form.driverName ?? ''}
              onChange={e => setField('driverName', e.target.value)} />
            <TextInput label="Phone" value={form.driverPhone ?? ''}
              onChange={e => setField('driverPhone', e.target.value)} />
            <TextInput label="Email" value={form.driverEmail ?? ''}
              onChange={e => setField('driverEmail', e.target.value)} />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Assignment</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Crew Name" value={form.crewName ?? ''}
              onChange={e => setField('crewName', e.target.value)}
              description="Auto-generated if left blank" />
            <Select label="Crew Type" data={CREW_TYPES} value={form.crewType || null}
              onChange={v => setField('crewType', v ?? '')} clearable />
            <Select label="Contractor" data={contractorSelect} value={form.contractorId || null}
              onChange={v => setField('contractorId', v ?? '')} searchable clearable />
            <Select label="Truck" data={truckSelect} value={form.truckId || null}
              onChange={v => setField('truckId', v ?? '')} searchable clearable />
            <Select label="Assigned Zone" data={zoneSelect} value={form.assignedZoneId || null}
              onChange={v => setField('assignedZoneId', v ?? '')} searchable clearable />
            <Select label="Status" data={STATUS_OPTIONS}
              value={form.status ?? 'active'}
              onChange={v => setField('status', v ?? 'active')} />
          </SimpleGrid>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />

          <Divider label="Driver Resources" labelPosition="left" />
          <Text size="xs" c="dimmed">Attach documents such as driver's license, certifications, or insurance cards (images or small PDFs, max 5 files).</Text>
          {/* Uploaded files list */}
          {(() => {
            let files: Array<{name: string; type: string; uploadedAt: string; data: string}> = [];
            try { files = JSON.parse(form.resourceFiles || '[]'); } catch { /* */ }
            return files.length > 0 ? (
              <Stack gap="xs">
                {files.map((f, i) => (
                  <Group key={i} gap="sm" p="xs" style={{ border: '1px solid #cbd5e1', borderRadius: 4 }}>
                    {f.type.startsWith('image/') && f.data ? (
                      <img src={f.data} alt={f.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                    ) : (
                      <ActionIcon variant="light" size="xl" color="var(--color-primary-700)" style={{ flexShrink: 0 }}>
                        <IconFile size={20} />
                      </ActionIcon>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</Text>
                      <Text size="xs" c="dimmed">{fmtDate(f.uploadedAt)}{!f.data ? ' — filename only (file too large)' : ''}</Text>
                    </div>
                    <Group gap={4}>
                      {f.data && (
                        <ActionIcon size="sm" variant="subtle" color="var(--color-primary-700)" title="Download" onClick={() => downloadFile(f)}>
                          <IconDownload size={14} />
                        </ActionIcon>
                      )}
                      <ActionIcon size="sm" variant="subtle" color="var(--color-error)" title="Remove" onClick={() => removeFile(i)}>
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                  </Group>
                ))}
              </Stack>
            ) : null;
          })()}
          <input ref={fileInputRef} type="file"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) { handleFileUpload(f); e.target.value = ''; } }} />
          <Button variant="outline" size="xs" leftSection={<IconUpload size={14} />}
            onClick={() => fileInputRef.current?.click()}>
            Attach File
          </Button>

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Add Driver'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove Driver" centered size="sm">
        <Stack>
          <Text size="sm">Remove driver <strong>{deleteTarget?.driverName}</strong> and their crew assignment?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Remove</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
